import api from './api';
import notificationService from './notification';
// Note: Import inactivity service dynamically to avoid circular dependency
let inactivityService = null;

export const authService = {
  /**
   * Register a new user
   */
  async register() {
    throw new Error('Self-sign up is disabled for Reconciler customers. Contact your company admin for account provisioning.');
  },

  /**
   * Login user
   */
  async login(credentials, options = {}) {
    try {
      const response = await api.post('/v1/reconciler/auth/login', {
        email: credentials.email,
        password: credentials.password,
      });

      if (response.data.success) {
        // Store tokens and user data
        this.storeAuthData(response.data.data);
        return response.data;
      }
      throw new Error(response.data.message || 'Login failed');
    } catch (error) {
      throw this.handleError(error, options);
    }
  },

  /**
   * Send forgot password email
   */
  async forgotPassword(email) {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Verify OTP
   */
  async verifyOTP(otp) {
    try {
      const response = await api.post('/auth/verify-otp', { otp });
      
      if (response.data.success) {
        // Update user data in storage
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        userData.is_otp_verified = true;
        localStorage.setItem('user', JSON.stringify(userData));
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Resend OTP to authenticated user
   */
  async resendOTP() {
    try {
      const response = await api.post('/auth/resend-otp');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Verify login OTP (2FA)
   */
  async verifyLoginOTP(otp) {
    try {
      const response = await api.post('/auth/verify-login-otp', { otp });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Resend login OTP (2FA)
   */
  async resendLoginOTP() {
    try {
      const response = await api.post('/auth/resend-login-otp');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Reset password with code
   */
  async resetPassword(data) {
    try {
      const response = await api.post('/auth/reset-password', {
        email: data.email,
        reset_code: data.reset_code,
        password: data.password,
        password_confirmation: data.password_confirmation,
      });
      if (response.data.success && response.data.data) {
        this.storeAuthData(response.data.data);
      }
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Change password for authenticated user
   */
  async changePassword(data) {
    try {
      const response = await api.post('/auth/change-password', {
        current_password: data.current_password,
        password: data.password,
        password_confirmation: data.password_confirmation,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Logout user
   */
  async logout() {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
    } finally {
      this.clearAuthData();
    }
  },

  /**
   * Get user profile
   */
  async getProfile() {
    try {
      const response = await api.get('/auth/profile');
      
      if (response.data.success) {
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
        return response.data;
      }
      throw new Error(response.data.message || 'Failed to get profile');
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Refresh authentication token
   */
  async refreshToken() {
    try {
      const response = await api.post('/auth/refresh');
      
      if (response.data.success) {
        localStorage.setItem('access_token', response.data.data.access_token);
        return response.data;
      }
      throw new Error('Token refresh failed');
    } catch (error) {
      this.clearAuthData();
      throw this.handleError(error);
    }
  },

  /**
   * Store authentication data in localStorage
   */
  storeAuthData(data) {
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('token_type', data.token_type || 'bearer');
    localStorage.setItem('expires_in', data.expires_in || '3600');
    this.unlockSession();
    
    // Start inactivity tracking after successful login
    this.startInactivityTracking();
  },

  /**
   * Clear authentication data from localStorage
   */
  clearAuthData() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('token_type');
    localStorage.removeItem('expires_in');
    localStorage.removeItem('reset-email-kanakku');
    localStorage.removeItem('requires_2fa_pending');
    localStorage.removeItem('session_locked');
    localStorage.removeItem('session_lock_redirect');
    
    // Stop inactivity tracking when clearing auth data
    this.stopInactivityTracking();
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    const token = localStorage.getItem('access_token');
    const user = localStorage.getItem('user');
    return !!(token && user);
  },

  /**
   * Get current access token
   */
  getToken() {
    return localStorage.getItem('access_token');
  },

  /**
   * Check if user requires 2FA verification
   */
  requires2FAVerification() {
    try {
      // Strong client-side latch to handle token-claim race
      if (localStorage.getItem('requires_2fa_pending') === '1') {
        return true;
      }

      const token = this.getToken();
      if (!token) return false;

      // Robust Base64URL decode for JWT payload
      const base64Url = token.split('.')[1];
      if (!base64Url) return false;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
      const payload = JSON.parse(atob(padded));

      if (payload && typeof payload.requires_2fa_verification !== 'undefined') {
        return payload.requires_2fa_verification === true;
      }
      if (payload && typeof payload.is_2fa_verified !== 'undefined') {
        return payload.is_2fa_verified === false;
      }
      return false;
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      return false;
    }
  },

  /**
   * Get current user data
   */
  getCurrentUser() {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  },

  /**
   * Lock authenticated session until password re-authentication
   */
  lockSession(redirectPath = '') {
    localStorage.setItem('session_locked', '1');
    const fallbackPath = redirectPath || '/dashboard';
    localStorage.setItem('session_lock_redirect', fallbackPath);
  },

  /**
   * Clear lock state after successful unlock
   */
  unlockSession() {
    localStorage.removeItem('session_locked');
    localStorage.removeItem('session_lock_redirect');
  },

  /**
   * Whether current session is locked
   */
  isSessionLocked() {
    return localStorage.getItem('session_locked') === '1';
  },

  /**
   * Return stored redirect path after unlock
   */
  getLockedRedirect() {
    return localStorage.getItem('session_lock_redirect') || '';
  },

  /**
   * Check if user needs OTP verification
   */
  needsOTPVerification() {
    const user = this.getCurrentUser();
    return user && !user.is_otp_verified;
  },

  /**
   * Handle API errors consistently with proper UI feedback
   */
  handleError(error, options = {}) {
    const notify = options.notify !== false;

    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || 'An error occurred';
      const errors = error.response.data?.errors;
      const status = error.response.status;

      // Handle different types of errors with appropriate UI
      if (status === 422 && errors && notify) {
        notificationService.validationErrors(errors);
      } else if (status === 401 && notify) {
        notificationService.error.toast('Invalid credentials. Please try again.', 'right', 5000);
      } else if (status === 403 && notify) {
        notificationService.warning.swal('Access Denied', message);
      } else if (status >= 500 && notify) {
        notificationService.networkError({ status, message });
      } else if (notify) {
        notificationService.error.toast(message);
      }

      return { message, errors, status };
    } else if (error.request) {
      // Network error
      const networkError = { message: 'Network error. Please check your connection.', status: 0 };
      if (notify) {
        notificationService.networkError(networkError);
      }
      return networkError;
    } else {
      // Other error
      const genericError = { message: error.message || 'An unexpected error occurred', status: 0 };
      if (notify) {
        notificationService.error.toast(genericError.message);
      }
      return genericError;
    }
  },

  /**
   * Show success notification
   */
  showSuccess(message, useModal = false) {
    if (useModal) {
      notificationService.success.swal('Success!', message);
    } else {
      notificationService.success.toast(message);
    }
  },

  /**
   * Show loading state
   */
  showLoading(title = 'Processing...', text = 'Please wait...') {
    return notificationService.loading.show(title, text);
  },

  /**
   * Hide loading state
   */
  hideLoading() {
    notificationService.loading.close();
  },

  /**
   * Start inactivity tracking
   */
  startInactivityTracking() {
    try {
      if (!inactivityService) {
        // Dynamically import to avoid circular dependency
        import('@/services/inactivity').then(module => {
          inactivityService = module.default;
          inactivityService.start();
        });
      } else {
        inactivityService.start();
      }
    } catch (error) {
      console.error('Failed to start inactivity tracking:', error);
    }
  },

  /**
   * Stop inactivity tracking
   */
  stopInactivityTracking() {
    try {
      if (inactivityService) {
        inactivityService.stop();
      }
    } catch (error) {
      console.error('Failed to stop inactivity tracking:', error);
    }
  }
};

export default authService;
